// Libs
import React from 'react';
import { connect } from 'react-redux';
import { selectProfile } from 'screens/Login/selectors';
import { selectSelectedSubModuleId } from 'components/Modules/selectors';
// Utils
import { GlobalUtils } from 'utils';
// Actions
import { mutate1Object as mutate1ObjectInCore, resetDisplay } from '../actions';
// Selectors
import {
	selectAuthorizedElements,
	selectAuthorizedScreens,
	selectScreenKey,
	selectSelectedDataFilterId,
	selectSelectedDataSourceConfig,
	selectSelectedScreenId,
	selectSelectedElementId,
} from '../selectors';
// Service
import { ApiService } from 'services';
// Components
import { Wrapper, Dropdown } from 'components';
import ElementPicker from '../ElementBuilder/ElementPicker';
import { ScreenBuilderConfig, ScreenPicker } from '../ScreenBuilder';
import {
	getBrowserStorage,
	readDisplayPreferences,
	resolveDisplaySelection,
	useViewportCategory,
	writeDisplayPreference,
} from './displayViewPolicy';

const DisplayOptionsMenu = ({
	selectedScreenId,
	selectedElementId,
	screens,
	elements,
	onMutateScreen,
	onMutateElement,
}) => {
	return (
		<Wrapper
			mediumBackground
			padding='10px'
			width='100%'
			flexDirection='column'
			maxWidth='200px'
		>
			{/* Iterate & Render all screen options */}
			<ScreenPicker
				selectedScreenId={selectedScreenId}
				screens={screens}
				onMutateScreen={onMutateScreen}
			/>
			{/* Iterate & Render all element options */}
			<ElementPicker
				selectedElementId={selectedElementId}
				elements={elements}
				onMutateElement={onMutateElement}
			/>
		</Wrapper>
	);
};

/**
 * @param {Object} display - The display current settings
 * @param {Object[]} display.screens - The screen array from selectedDataFilterId
 * @param {int} display.selectedScreenId
 * @param {int} selectedDataFilterId
 * @param {Object} selectedDataSourceConfig
 * @param {function} mutate1ObjectInCore - The mutate1ObjectInCore redux action
 * @returns {ReactNode}
 */
export const DisplayPicker = ({
	// State
	selectedDataFilterId,
	selectedDataSourceConfig,
	selectedScreenId,
	selectedElementId,
	screenKey,
	authorizedScreens,
	authorizedElements,
	profile,
	selectedSubModuleId,
	// Actions
	mutate1ObjectInCore,
	resetDisplay,
}) => {
	const displayRequestIdRef = React.useRef(0);
	const viewportCategory = useViewportCategory();
	const viewportCategoryRef = React.useRef(viewportCategory);
	viewportCategoryRef.current = viewportCategory;
	const previousViewportCategoryRef = React.useRef(viewportCategory);
	const userId = profile?.user?.id;
	const preferenceContextId = selectedDataFilterId
		? `${selectedSubModuleId ? `sub-module:${selectedSubModuleId}:` : ''}data-filter:${selectedDataFilterId}`
		: undefined;
	const preferenceScopeKey = `${userId ?? ''}:${preferenceContextId ?? ''}`;
	const latestDisplayContextRef = React.useRef();
	latestDisplayContextRef.current = {
		userId,
		preferenceContextId,
		preferenceScopeKey,
		selectedDataSourceConfig,
	};
	const previousPreferenceScopeRef = React.useRef(preferenceScopeKey);
	const previousDataFilterIdRef = React.useRef(selectedDataFilterId);

	const getPersistedSelection = category => {
		const context = latestDisplayContextRef.current;
		return readDisplayPreferences({
			userId: context.userId,
			contextId: context.preferenceContextId,
			storage: getBrowserStorage(),
		})[category];
	};

	const persistSelection = (category, selection) => {
		const context = latestDisplayContextRef.current;
		writeDisplayPreference({
			userId: context.userId,
			contextId: context.preferenceContextId,
			viewportCategory: category,
			selection,
			storage: getBrowserStorage(),
		});
	};

	// On Mutate display
	const onMutateScreen = selectedScreenId => {
		const selectedScreen = authorizedScreens.find(
			screen => screen.id === selectedScreenId,
		);
		const selection = resolveDisplaySelection({
			screens: authorizedScreens,
			viewportCategory,
			persistedSelection: {
				screenId: selectedScreenId,
				screenKey: selectedScreen?.key,
				explicit: true,
			},
		});
		persistSelection(viewportCategory, selection);
		mutate1ObjectInCore('display', {
			selectedScreenId: selection.screenId,
			selectedElementId: selection.elementId,
		});
	};
	const onMutateElement = selectedElementId => {
		const selectedScreen = authorizedScreens.find(
			screen => screen.id === selectedScreenId,
		);
		const selectedElement = authorizedElements.find(
			element => element.id === selectedElementId,
		);
		persistSelection(viewportCategory, {
			screenId: selectedScreenId,
			screenKey: selectedScreen?.key,
			elementId: selectedElementId,
			elementKey: selectedElement?.key,
			explicit: true,
		});
		mutate1ObjectInCore('display', { selectedElementId });
	};

	const sortScreens = ({ orderScreenByIds, authorizedScreens }) => {
		const authorizedScreensIds = authorizedScreens.map(screen => screen.id);
		const sortScreensIds = orderScreenByIds
			.concat(
				authorizedScreensIds.filter(
					screenId => !orderScreenByIds.includes(screenId),
				),
			)
			.filter(screenOrderIdx => authorizedScreensIds.includes(screenOrderIdx));

		return sortScreensIds.map(id =>
			authorizedScreens.find(screen => screen.id == id),
		);
	};

	// Get Screen & Element option list data
	React.useEffect(() => {
		const requestId = ++displayRequestIdRef.current;
		let isCurrentRequest = true;

		// A datasource owns its display configuration. Clear the previous one before
		// requesting the new options so stale screens cannot remain actionable.
		resetDisplay();
		if (!selectedDataFilterId) return;

		mutate1ObjectInCore('display', {
			isFetching: true,
		});

		ApiService.fetch(ApiService.endpoints().GET_FEED_DISPLAY_OPTIONS, {
			reqParams: { selectedDataFilterId },
		})
			.then(_screens => {
				if (!isCurrentRequest || requestId !== displayRequestIdRef.current)
					return;

				const orderScreenByIds =
					latestDisplayContextRef.current.selectedDataSourceConfig
						?.orderScreensIds;
				const authorizedScreens = ScreenBuilderConfig.getAuthorizedScreens(
					GlobalUtils.checkArray(_screens),
				);

				const screens = orderScreenByIds?.length
					? sortScreens({ orderScreenByIds, authorizedScreens })
					: authorizedScreens;

				const currentViewportCategory = viewportCategoryRef.current;
				previousViewportCategoryRef.current = currentViewportCategory;
				previousPreferenceScopeRef.current =
					latestDisplayContextRef.current.preferenceScopeKey;
				const selection = resolveDisplaySelection({
					screens,
					viewportCategory: currentViewportCategory,
					persistedSelection: getPersistedSelection(currentViewportCategory),
				});
				persistSelection(currentViewportCategory, selection);

				mutate1ObjectInCore('display', {
					isFetching: false,
					selectedScreenId: selection.screenId,
					selectedElementId: selection.elementId,
					screens,
				});
			})
			.catch(() => {
				if (!isCurrentRequest || requestId !== displayRequestIdRef.current)
					return;
				mutate1ObjectInCore('display', {
					isFetching: false,
					selectedScreenId: undefined,
					selectedElementId: undefined,
					screens: [],
				});
			});

		return () => {
			isCurrentRequest = false;
		};
	}, [selectedDataFilterId]);

	// Re-resolve when viewport category or user/module context changes. A data
	// filter change is owned by the request effect above to avoid using old screens.
	React.useEffect(() => {
		if (previousDataFilterIdRef.current !== selectedDataFilterId) {
			previousDataFilterIdRef.current = selectedDataFilterId;
			return;
		}
		const viewportChanged =
			previousViewportCategoryRef.current !== viewportCategory;
		const preferenceScopeChanged =
			previousPreferenceScopeRef.current !== preferenceScopeKey;
		if (!viewportChanged && !preferenceScopeChanged) return;
		if (!authorizedScreens.length) return;
		previousViewportCategoryRef.current = viewportCategory;
		previousPreferenceScopeRef.current = preferenceScopeKey;

		const selection = resolveDisplaySelection({
			screens: authorizedScreens,
			viewportCategory,
			persistedSelection: getPersistedSelection(viewportCategory),
		});
		persistSelection(viewportCategory, selection);
		mutate1ObjectInCore('display', {
			selectedScreenId: selection.screenId,
			selectedElementId: selection.elementId,
		});
	}, [
		viewportCategory,
		preferenceScopeKey,
		selectedDataFilterId,
		authorizedScreens,
	]);

	const Icon =
		ScreenBuilderConfig.getPickerIcon(screenKey) ||
		ScreenBuilderConfig.getPickerIcon(
			ScreenBuilderConfig.authorizedScreenKeys().standardGridV1,
		);

	return (
		<Dropdown.Wrapper
			trigger={['click']}
			overlay={
				<DisplayOptionsMenu
					selectedScreenId={selectedScreenId}
					selectedElementId={selectedElementId}
					screens={authorizedScreens}
					elements={authorizedElements}
					onMutateScreen={onMutateScreen}
					onMutateElement={onMutateElement}
				/>
			}
		>
			<Icon button selected />
		</Dropdown.Wrapper>
	);
};

const mapStateToProps = state => ({
	selectedDataFilterId: selectSelectedDataFilterId(state),
	selectedDataSourceConfig: selectSelectedDataSourceConfig(state),
	selectedScreenId: selectSelectedScreenId(state),
	selectedElementId: selectSelectedElementId(state),
	screenKey: selectScreenKey(state),
	authorizedScreens: selectAuthorizedScreens(state),
	authorizedElements: selectAuthorizedElements(state),
	profile: selectProfile(state),
	selectedSubModuleId: selectSelectedSubModuleId(state),
});
const actions = { mutate1ObjectInCore, resetDisplay };
export default connect(mapStateToProps, actions)(DisplayPicker);
